<script setup lang="ts">
  import { computed } from 'vue'
  import { CircleHelp } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics } from '@/composables/useMetrics'
  import { EventNameEnum } from '@/types/generated'

  interface Props {
    /** The knowledge base URL to open */
    url: string
    /** Optional custom tooltip text. Defaults to "Learn more" */
    tooltip?: string
    /** Optional location identifier for analytics tracking */
    location?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    tooltip: undefined,
    location: undefined
  })

  const { t } = useTranslation({
    en: {
      learnMore: 'Learn more'
    },
    de: {
      learnMore: 'Mehr erfahren'
    }
  })

  const { trackEvent } = useMetrics()

  const tooltipText = computed(() => props.tooltip || t('learnMore'))

  const handleClick = async () => {
    // Track analytics event
    try {
      await trackEvent(EventNameEnum.HELP_LINK_CLICKED, {
        help_link_url: props.url,
        help_link_location: props.location || 'unknown'
      })
    } catch (error) {
      // Silently fail analytics tracking - don't block user action
      console.error('Failed to track help link click:', error)
    }
  }
</script>

<template>
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger as-child>
        <a
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="tooltipText"
          class="inline-flex items-center rounded text-gray-500 transition-colors hover:text-gray-700
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          @click="handleClick"
        >
          <CircleHelp class="h-5 w-5" />
        </a>
      </TooltipTrigger>
      <TooltipContent variant="secondary">
        <p>{{ tooltipText }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
